import CornerstoneLogo from '@/assets/logos/CornerstoneLogo';
import classes from '@/styles/login.module.css';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { useSnackBarContext } from '@/utils/snackbar';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Link, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import Head from 'next/head';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import {
  useState,
  type ChangeEvent,
  type ReactElement,
  type SubmitEvent,
} from 'react';

function getFinishRegistrationErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return 'Failed to finish registration.';
  }

  const rawMessage = error.response?.data?.message;
  if (typeof rawMessage === 'string') {
    try {
      const parsed = JSON.parse(rawMessage) as { message?: string };
      if (parsed?.message) {
        return parsed.message;
      }
    } catch {
      return rawMessage;
    }
    return rawMessage;
  }

  if (
    rawMessage &&
    typeof rawMessage === 'object' &&
    'message' in rawMessage &&
    typeof (rawMessage as { message?: unknown }).message === 'string'
  ) {
    return (rawMessage as { message: string }).message;
  }

  return 'Failed to finish registration.';
}

export function FinishRegistration(): ReactElement {
  const router = useRouter();
  const { updateSnack } = useSnackBarContext();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  async function sendPasswordEmail(trimmedEmail: string): Promise<void> {
    const newAxios = axios.create();
    await newAxios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_INVOKE_URL}/finishRegistration`,
      data: { email: trimmedEmail },
    });

    updateSnack({
      severity: 'success',
      message:
        'If the email provided is valid you will get a message with a new password to use to log into the site.',
    });

    setTimeout(() => {
      void router.push('/login');
    }, 5000);
  }

  async function submitForm(): Promise<void> {
    setLoading(true);
    try {
      await sendPasswordEmail(email.trim());
    } catch (error) {
      updateSnack({
        severity: 'error',
        message: getFinishRegistrationErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    setEmail(event.target.value);
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submitForm();
  }

  return (
    <CustomThemeProvider>
      <Head>
        <title>Finish Registration | Cornerstone Upstream</title>
      </Head>
      <Grid
        container
        spacing={0}
        direction='column'
        alignItems='center'
        className={classes.wrapper}
        style={{
          backgroundImage: `url("${process.env.NEXT_PUBLIC_STATIC_ASSETS_URL}cis-hub-bg.jpg")`,
        }}
      >
        <Box
          m='auto'
          width='80%'
          maxWidth={450}
          display='flex'
          flexDirection='column'
          alignItems='center'
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 400,
              display: 'flex',
              marginBottom: 3,
            }}
          >
            <CornerstoneLogo white />
          </Box>
          <Box
            component={Paper}
            width='100%'
            bgcolor='background.paper'
            justifyContent='center'
            display='flex'
            elevation={12}
            className={classes.paper}
          >
            <Box
              component='form'
              className={classes.form}
              onSubmit={handleSubmit}
            >
              <Typography component='h1' variant='h5' sx={{ mb: 5 }}>
                Finish Registration Process
              </Typography>
              <TextField
                variant='outlined'
                onChange={handleInputChange}
                placeholder='Enter your email address'
                label='Email'
                name='email'
                type='email'
                value={email}
                className={classes.input}
                fullWidth
              />
              <LoadingButton
                loading={loading}
                color='primary'
                variant='contained'
                type='submit'
                disabled={!email.trim() || loading}
                fullWidth
                sx={{ mt: 1 }}
              >
                Submit
              </LoadingButton>
              <Link
                component={NextLink}
                href='/login'
                color='primary'
                sx={{ display: 'inline-block', mt: 1.5 }}
              >
                Return to login
              </Link>
            </Box>
          </Box>
        </Box>
      </Grid>
    </CustomThemeProvider>
  );
}

export default FinishRegistration;
